
import { useState } from 'react';
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

export const useInvoiceGeneration = () => {
  const [invoiceData, setInvoiceData] = useState<{ invoiceNumber: string; pdfBase64: string } | null>(null);
  const [showInvoice, setShowInvoice] = useState(false);
  const { toast } = useToast();
  
  const generateInvoiceNumber = () => {
    const prefix = 'QUO'; // Using QUO for quotation instead of INV
    const timestamp = Date.now().toString().slice(-6);
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `${prefix}-${timestamp}-${random}`;
  };

  const generateInvoice = async (
    campaignId: string,
    companyDetails: any,
    amount: number,
    paymentMethod: string,
    devices: number,
    duration: number,
    selectedLocations: any[],
    budgetCalculations?: any
  ) => {
    try {
      const invoiceNumber = generateInvoiceNumber();
      const paymentDate = new Date().toISOString();
      
      // Ensure we have company details
      if (!companyDetails || Object.keys(companyDetails).length === 0) {
        // Attempt to load from localStorage as a fallback
        const storedCompanyDetails = localStorage.getItem('companyDetails');
        if (storedCompanyDetails) {
          companyDetails = JSON.parse(storedCompanyDetails);
          console.log("Loaded company details from localStorage:", companyDetails);
        } else {
          const formData = localStorage.getItem('formData');
          if (formData) {
            companyDetails = JSON.parse(formData);
            console.log("Loaded company details from formData:", companyDetails);
          } else {
            console.error("No company details found in localStorage");
            companyDetails = {
              companyName: "Company Name Missing",
              brandName: "Brand Name Missing",
              personInCharge: "Contact Person Missing",
              jobTitle: "Position Missing",
              email: "Email Missing",
              mobile: "Mobile Missing"
            };
          }
        }
      }
      
      // Double-check that values are not empty strings or null/undefined
      const requiredFields = ['companyName', 'brandName', 'personInCharge', 'jobTitle', 'email', 'mobile'];
      const defaultValues = {
        companyName: "Company Name Missing",
        brandName: "Brand Name Missing",
        personInCharge: "Contact Person Missing",
        jobTitle: "Position Missing",
        email: "Email Missing",
        mobile: "Mobile Missing"
      };
      
      for (const field of requiredFields) {
        if (!companyDetails[field] || companyDetails[field] === '') {
          companyDetails[field] = defaultValues[field as keyof typeof defaultValues];
        }
      }
      
      // Get budget calculations if available and not provided
      if (!budgetCalculations) {
        const budgetCalculationsStr = localStorage.getItem('budgetCalculations');
        budgetCalculations = budgetCalculationsStr ? JSON.parse(budgetCalculationsStr) : null;
      }

      console.log("Calling generate-invoice function with company details:", companyDetails);
      console.log("Calling generate-invoice function with data:", { 
        campaignId, 
        invoiceNumber, 
        amount, 
        devices, 
        duration, 
        locationCount: selectedLocations.length,
        companyDetails: companyDetails,
        budgetCalculations: budgetCalculations ? "Present" : "Not available"
      });

      const { data, error } = await supabase.functions.invoke('generate-invoice', {
        body: {
          campaignId,
          invoiceNumber,
          companyDetails,
          companyInfo: companyDetails, // Add this as a backup
          amount,
          paymentMethod,
          paymentDate,
          devices,
          duration,
          selectedLocations,
          budgetCalculations // Pass budget calculations for accurate financials
        }
      });

      if (error) {
        console.error('Error generating invoice:', error);
        toast({
          title: "Invoice Generation Failed",
          description: "There was an error generating your invoice. Please try again.",
          variant: "destructive"
        });
        return null;
      }

      console.log('Invoice generated successfully:', data);
      
      if (paymentMethod !== 'quotation') {
        // Record invoice in the database only for actual payments, not quotations
        const { error: dbError } = await supabase
          .from('campaign_payments')
          .insert({
            campaign_id: campaignId,
            amount: amount,
            payment_method: paymentMethod,
            transaction_id: invoiceNumber,
            status: 'paid',
            payment_date: paymentDate
          });
  
        if (dbError) {
          console.error('Error recording invoice in database:', dbError);
        }
      }

      return data;
    } catch (error) {
      console.error('Error in generateInvoice:', error);
      toast({
        title: "Invoice Generation Failed",
        description: "There was an error generating your invoice. Please try again.",
        variant: "destructive"
      });
      return null;
    }
  };

  const closeInvoiceAndReset = (resetForm: () => void) => {
    setShowInvoice(false);
    setInvoiceData(null);
    resetForm();
  };

  const downloadInvoice = () => {
    if (!invoiceData) return;
    
    const linkSource = `data:application/pdf;base64,${invoiceData.pdfBase64}`;
    const downloadLink = document.createElement("a");
    const fileName = `${invoiceData.invoiceNumber}.pdf`;

    downloadLink.href = linkSource;
    downloadLink.download = fileName;
    downloadLink.click();
  };

  return {
    invoiceData,
    showInvoice,
    setInvoiceData,
    setShowInvoice,
    generateInvoice,
    closeInvoiceAndReset,
    downloadInvoice
  };
};
